// Function index:
//   HudOptions — { reload? } — injection seam for the map-style switch.
//   mountHud(opts) — wire the in-game chrome that isn't the map or chat:
//       the right slide-out actions tray (open/close, auto-close when an
//       action opens its panel) and the options gear popover (text size,
//       reduce motion, MAP STYLE, chat auto-scroll, chat timestamps). The
//       Leave/Log-out button inside the popover is wired by runtime.ts.
//
// Why: keeps tray + options behaviour out of runtime.ts. Display prefs are
// persisted in localStorage and re-applied here on mount so they survive a
// reload. Map style is the one pref that can't apply in place — the renderer
// is chosen when the Pixi scene mounts — so it persists and reloads.

import { setChatAutoScroll, setChatTimestamps } from '../chat.js';

export interface HudOptions {
  // Injected so the map-style switch is testable; production passes a real
  // page reload, which is what actually rebuilds the Pixi scene.
  reload?: () => void;
}

export function mountHud(opts: HudOptions = {}): void {
  const reload = opts.reload ?? ((): void => { window.location.reload(); });
  const $ = (id: string): HTMLElement => document.getElementById(id)!;
  const tray = $('tray');
  const trayTab = $('tray-tab');
  const trayClose = $('tray-close');
  const gear = $('gear-btn');
  const popover = $('options-popover');
  const narration = $('narration');

  // ── actions tray ──────────────────────────────────────────────────────
  const openTray = (open: boolean): void => { tray.classList.toggle('open', open); };
  trayTab.addEventListener('click', () => openTray(!tray.classList.contains('open')));
  trayClose.addEventListener('click', () => openTray(false));
  // Clicking a panel action (Map / Sheet / Quests / Journal) opens a modal
  // overlay; collapse the tray behind it. (Dice keeps the tray open — its
  // popover lives inside the tray.)
  for (const id of ['map-btn', 'sheet-btn', 'quests-btn', 'journal-btn', 'ledger-btn', 'chronicle-btn', 'board-btn', 'rest-btn', 'shop-btn']) {
    document.getElementById(id)?.addEventListener('click', () => openTray(false));
  }

  // ── options gear ──────────────────────────────────────────────────────
  const toggleOptions = (show?: boolean): void => {
    popover.hidden = show === undefined ? !popover.hidden : !show;
  };
  gear.addEventListener('click', (e) => { e.stopPropagation(); toggleOptions(); });
  // Dismiss on outside click / Escape.
  document.addEventListener('click', (e) => {
    if (popover.hidden) return;
    if (!popover.contains(e.target as Node) && e.target !== gear) toggleOptions(false);
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') toggleOptions(false); });

  // Text size — segmented sm / md / lg, applied as a class on #narration.
  const sizeSeg = $('opt-textsize');
  const applySize = (size: string): void => {
    narration.classList.remove('text-sm', 'text-md', 'text-lg');
    if (size !== 'md') narration.classList.add(`text-${size}`);
    for (const b of sizeSeg.querySelectorAll('button')) b.classList.toggle('active', b.dataset.size === size);
    localStorage.setItem('rz.textsize', size);
  };
  for (const b of sizeSeg.querySelectorAll<HTMLButtonElement>('button')) {
    b.addEventListener('click', () => applySize(b.dataset.size!));
  }
  applySize(localStorage.getItem('rz.textsize') ?? 'md');

  // Map style — painterly (default) / iso / plain. The renderer is chosen when
  // mountHexScene builds the Pixi app, so switching can't take effect in place;
  // persist the choice and reload, which remounts the scene against it. Nothing
  // is lost — the whole scene is rebuilt from server state on load.
  const styleSeg = $('opt-mapstyle');
  const styleButtons = [...styleSeg.querySelectorAll<HTMLButtonElement>('button')];
  const markStyle = (style: string): void => {
    for (const b of styleButtons) b.classList.toggle('active', b.dataset.style === style);
  };
  const stored = localStorage.getItem('rz.mapstyle');
  // Unknown/absent values fall back to the default, matching atlas.ts::mapStyle.
  let activeStyle = styleButtons.some((b) => b.dataset.style === stored) ? stored! : 'painterly';
  markStyle(activeStyle);
  for (const b of styleButtons) {
    b.addEventListener('click', () => {
      const next = b.dataset.style!;
      if (next === activeStyle) return; // no pointless reload
      activeStyle = next;
      localStorage.setItem('rz.mapstyle', next);
      markStyle(next);
      reload();
    });
  }

  // Toggle switches.
  bindToggle($('opt-motion'), localStorage.getItem('rz.reducemotion') === '1', (on) => {
    document.body.classList.toggle('reduce-motion', on);
    localStorage.setItem('rz.reducemotion', on ? '1' : '0');
  });
  bindToggle($('opt-autoscroll'), localStorage.getItem('rz.chat.autoscroll') !== '0', (on) => setChatAutoScroll(on));
  bindToggle($('opt-timestamps'), localStorage.getItem('rz.chat.timestamps') === '1', (on) => setChatTimestamps(on));
}

// A pill switch with .on state; calls onChange with the new value and runs
// it once on mount to apply the persisted value.
function bindToggle(el: HTMLElement, initial: boolean, onChange: (on: boolean) => void): void {
  const set = (on: boolean): void => { el.classList.toggle('on', on); onChange(on); };
  el.addEventListener('click', () => set(!el.classList.contains('on')));
  el.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); set(!el.classList.contains('on')); }
  });
  set(initial);
}
